import type { Locale } from "date-fns";
import { format, formatDistanceToNow } from "date-fns";

export type DateInput = Date | number | string;

/**
 * `format` throws a RangeError on an invalid date where `toLocaleString` only
 * printed "Invalid Date": one malformed birthday would take the whole contact
 * file down with it. An unreadable date renders as nothing instead.
 */
const valid = (date: DateInput): Date | null => {
  const d = new Date(date);
  return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * Every date the back-office shows is formatted through here, with the
 * language the user reads the interface in. `format(date, "PPP")` alone and
 * `toLocaleDateString("en-US")` both print English in a French interface —
 * "July 10, 2026" under "Ajouté" — and a bare `toLocaleString()` follows the
 * browser rather than the user's own preference, which is no better.
 *
 * Pure on purpose (no hook, no i18next), so it runs under `node --test`;
 * `useDateFormat()` in `i18n.ts` binds it to the current language.
 */
export const createDateFormat = (locale: Locale) => {
  // date-fns names a locale "fr" / "en-US"; Intl takes both as they are.
  const intl = locale.code || "en-US";
  return {
    locale,
    intl,
    format: (date: DateInput, pattern: string) => {
      const d = valid(date);
      return d ? format(d, pattern, { locale }) : "";
    },
    /** "il y a 3 heures" / "3 hours ago". */
    fromNow: (date: DateInput) => {
      const d = valid(date);
      return d ? formatDistanceToNow(d, { addSuffix: true, locale }) : "";
    },
    /**
     * As short as a list column can make it: "14:32" today, "12 oct." this
     * year, "12 oct. 2025" before — the way a mail client dates its rows.
     * "il y a 3 heures" took three times the width to say less.
     */
    short: (date: DateInput, now: DateInput = Date.now()) => {
      const d = valid(date);
      if (!d) return "";
      const today = new Date(now);
      if (d.toDateString() === today.toDateString())
        return new Intl.DateTimeFormat(intl, { timeStyle: "short" }).format(d);
      return new Intl.DateTimeFormat(intl, {
        day: "numeric",
        month: "short",
        ...(d.getFullYear() === today.getFullYear() ? {} : { year: "numeric" }),
      }).format(d);
    },
    /**
     * "29 août – 28 sept." / "Aug 29 – Sep 28". `formatRange` knows each
     * language's own order and only repeats what changes, which two
     * hand-joined halves never did. The year only when it is not this one:
     * the dashboard's selector has room for the range and nothing more, and
     * clipping it there is what left "Aug 29 - Sep 28," on screen.
     */
    range: (from: DateInput, to: DateInput, now: DateInput = Date.now()) => {
      const [a, b] = [valid(from), valid(to)];
      if (!a || !b) return "";
      const thisYear = new Date(now).getFullYear();
      const sameYear =
        a.getFullYear() === thisYear && b.getFullYear() === thisYear;
      return new Intl.DateTimeFormat(intl, {
        day: "numeric",
        month: "short",
        ...(sameYear ? {} : { year: "numeric" }),
      }).formatRange(a, b);
    },
  };
};

export type DateFormat = ReturnType<typeof createDateFormat>;
